/**
 * Path-sample helpers — convert a chain + a [startMm, endMm] range into a
 * world-frame polyline of Vector3s. Used by Stations, Dimensions and
 * InfoBars to build TubeGeometries that follow the path.
 */

import { Vector3 } from 'three';
import { moverWorldAt, type BuiltChain } from './ChainBuilder.js';

const DEG2RAD = Math.PI / 180;

/**
 * Sample a chain in part-local coords from startMm to endMm at approx.
 * `stepMm` spacing. Returns at least 2 points; clamps to [0, trackLength].
 *
 * Offsets:
 *   - `liftZ`     : vertical displacement (mm), added to Z. Used for tubes
 *                   that should sit above/below the path centerline.
 *   - `lateralMm` : lateral displacement (mm) along the path's left-hand
 *                   normal in the XY plane (+Y in path-frame). Lets you
 *                   render a parallel offset tube that, on curves, faithfully
 *                   tracks the centerline arc-by-arc — i.e. each sample at
 *                   centerline position `s` becomes the perpendicular point
 *                   at the same `s`, so labels still match centerline mm.
 */
export function sampleChainRange(
  chain: BuiltChain,
  startMm: number,
  endMm: number,
  stepMm = 5,
  liftZ = 0,
  lateralMm = 0,
): Vector3[] {
  const total = chain.trackLengthMm;
  if (total === 0) return [new Vector3(), new Vector3()];
  const start = Math.max(0, Math.min(startMm, total));
  const end = Math.max(0, Math.min(endMm, total));
  const range = Math.abs(end - start);
  const direction = end >= start ? 1 : -1;
  const steps = Math.max(2, Math.ceil(range / stepMm) + 1);
  const out: Vector3[] = new Array(steps);
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const s = start + direction * t * range;
    const pose = moverWorldAt(chain, s);
    if (!pose) {
      out[i] = new Vector3();
      continue;
    }
    const yaw = pose.yawDeg * DEG2RAD;
    // Left-hand normal in XY plane: rotate tangent +90° around Z.
    const nx = lateralMm === 0 ? 0 : -Math.sin(yaw) * lateralMm;
    const ny = lateralMm === 0 ? 0 : Math.cos(yaw) * lateralMm;
    out[i] = new Vector3(
      pose.positionMm[0] + nx,
      pose.positionMm[1] + ny,
      pose.positionMm[2] + liftZ,
    );
  }
  return out;
}

/**
 * Sample at a single position; returns part-local position + tangent.
 * Used by markers (Diamond / Tick) to orient themselves along the path.
 *
 * `liftZ` and `lateralMm` semantics match sampleChainRange — see there.
 */
export function sampleChainPoint(
  chain: BuiltChain,
  positionMm: number,
  liftZ = 0,
  lateralMm = 0,
): { position: Vector3; tangent: Vector3 } | null {
  const pose = moverWorldAt(chain, positionMm);
  if (!pose) return null;
  const yaw = pose.yawDeg * DEG2RAD;
  const tangent = new Vector3(Math.cos(yaw), Math.sin(yaw), 0);
  // Left-hand normal in XY plane: rotate tangent +90° around Z.
  const nx = lateralMm === 0 ? 0 : -Math.sin(yaw) * lateralMm;
  const ny = lateralMm === 0 ? 0 : Math.cos(yaw) * lateralMm;
  const position = new Vector3(
    pose.positionMm[0] + nx,
    pose.positionMm[1] + ny,
    pose.positionMm[2] + liftZ,
  );
  return { position, tangent };
}
